import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import './VigilRuleSmith.css';

/* ── severity palette (matches VigilScan) ──────────────────────────────── */
const SEV = {
  critical: { color: '#dc2626', bg: 'rgba(220,38,38,.10)', icon: '🔴' },
  high:     { color: '#ea580c', bg: 'rgba(234,88,12,.10)',  icon: '🟠' },
  medium:   { color: '#ca8a04', bg: 'rgba(202,138,4,.10)',  icon: '🟡' },
  low:      { color: '#2563eb', bg: 'rgba(37,99,235,.10)',  icon: '🔵' },
};

const LANGUAGES = ['go', 'java', 'python', 'javascript', 'typescript', 'c', 'cpp'];
const LANG_LABELS = { go: 'Go', java: 'Java', python: 'Python', javascript: 'JavaScript', typescript: 'TypeScript', c: 'C', cpp: 'C++' };

const PIPELINE_STEPS = [
  { key: 'generating',  label: 'Generate',  icon: '🤖', desc: 'AI writes the rule' },
  { key: 'validating',  label: 'Validate',  icon: '✓',  desc: 'Syntax & semantics' },
  { key: 'testing',     label: 'Test',      icon: '🧪', desc: 'Fixture verification' },
  { key: 'done',        label: 'Score',     icon: '📊', desc: 'Quality assessment' },
];

function statusIndex(s) {
  const map = { pending: -1, generating: 0, validating: 1, testing: 2, calibrating: 3, done: 3, approved: 3, failed: -2 };
  return map[s] ?? -1;
}

function statusBadge(status) {
  const colors = {
    pending: '#6b7280', generating: '#8b5cf6', validating: '#3b82f6',
    testing: '#f59e0b', calibrating: '#06b6d4', done: '#10b981',
    failed: '#ef4444', approved: '#059669',
  };
  return { backgroundColor: colors[status] || '#6b7280' };
}

function scoreColor(score) {
  if (score >= 70) return '#10b981';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
}

function parseJSON(str) {
  if (!str) return null;
  if (typeof str === 'object') return str;
  try { return JSON.parse(str); } catch { return null; }
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Quality Score Gauge — SVG radial gauge                                  */
/* ══════════════════════════════════════════════════════════════════════════ */
function QualityGauge({ score }) {
  const r = 54, stroke = 10, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = circ - (pct / 100) * circ;
  const color = scoreColor(pct);

  return (
    <div className="rulesmith-gauge">
      <svg viewBox="0 0 140 140" className="rulesmith-gauge__svg">
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--bs-border-color, #333)" strokeWidth={stroke} opacity="0.25" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 70 70)" className="rulesmith-gauge__arc" />
        <text x="70" y="64" textAnchor="middle" fill={color} fontSize="28" fontWeight="700" className="rulesmith-gauge__num">{pct}</text>
        <text x="70" y="84" textAnchor="middle" fill="var(--bs-secondary-color, #999)" fontSize="11" fontWeight="500">Quality</text>
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Pipeline Visualizer                                                     */
/* ══════════════════════════════════════════════════════════════════════════ */
function PipelineViz({ status }) {
  const idx = statusIndex(status);
  const failed = status === 'failed';

  return (
    <div className="rulesmith-pipeline">
      {PIPELINE_STEPS.map((step, i) => {
        const isActive = !failed && idx === i;
        const isDone = !failed && idx > i;
        const isFail = failed && idx === -2 && i === 0;
        let cls = 'rulesmith-pipeline__step';
        if (isDone) cls += ' rulesmith-pipeline__step--done';
        if (isActive) cls += ' rulesmith-pipeline__step--active';
        if (isFail) cls += ' rulesmith-pipeline__step--failed';

        return (
          <React.Fragment key={step.key}>
            {i > 0 && <div className={`rulesmith-pipeline__connector ${isDone || isActive ? 'rulesmith-pipeline__connector--lit' : ''}`} />}
            <div className={cls}>
              <div className="rulesmith-pipeline__icon">
                {isDone ? '✓' : isFail ? '✕' : step.icon}
              </div>
              <span className="rulesmith-pipeline__label">{step.label}</span>
              <span className="rulesmith-pipeline__desc">{step.desc}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  CalibrationModal                                                        */
/* ══════════════════════════════════════════════════════════════════════════ */
function CalibrationModal({ job, token, onClose, onDone }) {
  const [repoPath, setRepoPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rulesmith/calibrate`,
        { jobId: job.id, repoPath },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (onDone) onDone();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const cal = result ? parseJSON(result.calibrationRes || result) : null;

  return (
    <div className="rulesmith-modal-backdrop" onClick={onClose}>
      <div className="rulesmith-modal" onClick={e => e.stopPropagation()}>
        <div className="rulesmith-modal__header">
          <h3>⚗️ Calibrate Rule</h3>
          <button className="rulesmith-modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="rulesmith-modal__sub">Run <strong>{job.ruleId || job.id}</strong> against a real repository to measure false-positive rate.</p>

        <label className="rulesmith-label">Repository Path</label>
        <input className="vigil-input rulesmith-input" placeholder="/path/to/repo or https://github.com/..." value={repoPath} onChange={e => setRepoPath(e.target.value)} />

        {error && <div className="rulesmith-error">{error}</div>}

        <button className="vigil-btn-start rulesmith-btn" onClick={run} disabled={loading || !repoPath}>
          {loading ? <><span className="rulesmith-spinner" /> Calibrating…</> : 'Run Calibration'}
        </button>

        {cal && (
          <div className="rulesmith-cal-results">
            <div className="rulesmith-cal-stat">
              <span className="rulesmith-cal-stat__num">{cal.totalFindings ?? '—'}</span>
              <span className="rulesmith-cal-stat__label">Total Findings</span>
            </div>
            <div className="rulesmith-cal-stat rulesmith-cal-stat--tp">
              <span className="rulesmith-cal-stat__num">{cal.tpCount ?? '—'}</span>
              <span className="rulesmith-cal-stat__label">True Positives</span>
            </div>
            <div className="rulesmith-cal-stat rulesmith-cal-stat--fp">
              <span className="rulesmith-cal-stat__num">{cal.fpCount ?? '—'}</span>
              <span className="rulesmith-cal-stat__label">False Positives</span>
            </div>
            <div className="rulesmith-cal-bar">
              <div className="rulesmith-cal-bar__fill rulesmith-cal-bar__fill--tp"
                style={{ width: cal.totalFindings ? `${((cal.tpCount || 0) / cal.totalFindings) * 100}%` : '0%' }} />
              <div className="rulesmith-cal-bar__fill rulesmith-cal-bar__fill--fp"
                style={{ width: cal.totalFindings ? `${((cal.fpCount || 0) / cal.totalFindings) * 100}%` : '0%' }} />
            </div>
            <div className="rulesmith-cal-fprate">FP Rate: <strong>{cal.fpRate || '0%'}</strong></div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Main Component                                                          */
/* ══════════════════════════════════════════════════════════════════════════ */
export default function VigilRuleSmith() {
  const { t } = useTranslation();
  const { token } = useAuth();

  /* ── form state ──────────────────────────────────────────────────────── */
  const [description, setDescription] = useState('');
  const [languages, setLanguages] = useState([]);
  const [severity, setSeverity] = useState('high');

  /* ── job tracking ────────────────────────────────────────────────────── */
  const [activeJob, setActiveJob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const pollRef = useRef(null);

  /* ── history ─────────────────────────────────────────────────────────── */
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sortCol, setSortCol] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  /* ── calibration ─────────────────────────────────────────────────────── */
  const [calJob, setCalJob] = useState(null);

  /* ── detail view ─────────────────────────────────────────────────────── */
  const [detailJob, setDetailJob] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  /* ── fetch history ───────────────────────────────────────────────────── */
  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rulesmith/history?limit=50`, { headers });
      setHistory(data.jobs || []);
    } catch { /* ignore */ }
    setHistLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadHistory(); }, [loadHistory]);

  /* ── poll active job ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!activeJob) return;
    const active = ['pending', 'generating', 'validating', 'testing', 'calibrating'];
    if (!active.includes(activeJob.status)) { clearInterval(pollRef.current); return; }

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${config.BASE_URL}/vigil/rulesmith/history?limit=50`, { headers });
        const jobs = data.jobs || [];
        const updated = jobs.find(j => j.id === activeJob.id);
        if (updated) {
          setActiveJob(updated);
          if (!active.includes(updated.status)) {
            clearInterval(pollRef.current);
            loadHistory();
          }
        }
      } catch { /* ignore */ }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── generate ────────────────────────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!description.trim() || languages.length === 0) return;
    setGenerating(true); setGenError(''); setDetailJob(null);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rulesmith/generate`,
        { description: description.trim(), languages, severity },
        { headers });
      setActiveJob({ id: data.id, status: data.status || 'pending', description: description.trim() });
    } catch (e) {
      setGenError(e.response?.data?.error || e.message);
    }
    setGenerating(false);
  };

  /* ── approve ─────────────────────────────────────────────────────────── */
  const handleApprove = async (job) => {
    const packId = prompt('Enter rule pack ID to install to (e.g. "custom")');
    if (!packId) return;
    try {
      await axios.post(`${config.BASE_URL}/vigil/rulesmith/approve`,
        { jobId: job.id, packId },
        { headers });
      loadHistory();
      if (activeJob?.id === job.id) setActiveJob(prev => ({ ...prev, status: 'approved', approved: true, packId }));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  /* ── toggle language ─────────────────────────────────────────────────── */
  const toggleLang = (lang) => setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);

  /* ── sort history ────────────────────────────────────────────────────── */
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...history].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'qualityScore') { av = av || 0; bv = bv || 0; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  /* ── determine which job to show in detail ───────────────────────────── */
  const displayJob = detailJob || activeJob;
  const testResults = displayJob ? parseJSON(displayJob.testResults) : null;
  const calResults = displayJob ? parseJSON(displayJob.calibrationRes) : null;

  /* ── render ──────────────────────────────────────────────────────────── */
  return (
    <div className="rulesmith">
      {/* ▸ HEADER */}
      <div className="rulesmith-header">
        <div className="rulesmith-header__title-row">
          <span className="rulesmith-header__icon">🛡️</span>
          <div>
            <h2 className="rulesmith-header__title">
              {t('vigilRuleSmith.title', 'RuleSmith')}
            </h2>
            <p className="rulesmith-header__sub">
              {t('vigilRuleSmith.subtitle', 'AI-Powered Rule Factory — describe a vulnerability, get a production-ready detection rule')}
            </p>
          </div>
        </div>
      </div>

      <div className="rulesmith-body">
        {/* ▸ GENERATION PANEL */}
        <div className="vigil-card rulesmith-gen">
          <h3 className="rulesmith-section-title">⚡ Forge a Rule</h3>

          <label className="rulesmith-label">Description</label>
          <textarea className="vigil-input rulesmith-textarea" rows={3}
            placeholder="Describe what security pattern to detect…  e.g. 'Find SQL injection via string concatenation in prepared statement arguments'"
            value={description} onChange={e => setDescription(e.target.value)} />

          <label className="rulesmith-label">Languages</label>
          <div className="rulesmith-langs">
            {LANGUAGES.map(l => (
              <button key={l} type="button"
                className={`rulesmith-lang-chip ${languages.includes(l) ? 'rulesmith-lang-chip--active' : ''}`}
                onClick={() => toggleLang(l)}>
                {LANG_LABELS[l]}
              </button>
            ))}
          </div>

          <label className="rulesmith-label">Severity</label>
          <div className="rulesmith-sevs">
            {Object.keys(SEV).map(s => (
              <button key={s} type="button"
                className={`rulesmith-sev-chip ${severity === s ? 'rulesmith-sev-chip--active' : ''}`}
                style={severity === s ? { background: SEV[s].bg, borderColor: SEV[s].color, color: SEV[s].color } : {}}
                onClick={() => setSeverity(s)}>
                {SEV[s].icon} {s}
              </button>
            ))}
          </div>

          {genError && <div className="rulesmith-error">{genError}</div>}

          <button className="vigil-btn-start rulesmith-btn rulesmith-btn--generate"
            disabled={generating || !description.trim() || languages.length === 0}
            onClick={handleGenerate}>
            {generating ? <><span className="rulesmith-spinner" /> Generating…</> : '🔨 Generate Rule'}
          </button>
        </div>

        {/* ▸ PIPELINE */}
        {displayJob && (
          <div className="vigil-card rulesmith-pipeline-card">
            <h3 className="rulesmith-section-title">Pipeline</h3>
            <PipelineViz status={displayJob.status} />
            {displayJob.status === 'failed' && displayJob.error && (
              <div className="rulesmith-error" style={{ marginTop: '1rem' }}>❌ {displayJob.error}</div>
            )}
          </div>
        )}

        {/* ▸ RESULTS */}
        {displayJob && ['done', 'approved', 'calibrating'].includes(displayJob.status) && (
          <div className="vigil-card rulesmith-results">
            <h3 className="rulesmith-section-title">📋 Results — {displayJob.ruleId || displayJob.id}</h3>

            <div className="rulesmith-results__grid">
              {/* Rule content */}
              <div className="rulesmith-results__rule">
                <h4>Rule Content</h4>
                <pre className="rulesmith-code"><code>{displayJob.ruleContent || '(no content)'}</code></pre>
              </div>

              {/* Score + Test + Validation */}
              <div className="rulesmith-results__meta">
                <QualityGauge score={displayJob.qualityScore} />

                {/* Validation badge */}
                <div className="rulesmith-badge-row">
                  <span className={`rulesmith-badge ${displayJob.validationOk ? 'rulesmith-badge--ok' : 'rulesmith-badge--fail'}`}>
                    {displayJob.validationOk ? '✅ Valid' : '❌ Invalid'}
                  </span>
                  {displayJob.validationMsg && <span className="rulesmith-badge-msg">{displayJob.validationMsg}</span>}
                </div>

                {/* Test results */}
                {testResults && (
                  <div className="rulesmith-test-card">
                    <h4>Test Results</h4>
                    <div className="rulesmith-test-row">
                      <span className={testResults.vulnerableFindings > 0 ? 'rulesmith-test--pass' : 'rulesmith-test--warn'}>
                        {testResults.vulnerableFindings > 0 ? '✅' : '⚠️'} Vulnerable: {testResults.vulnerableFindings ?? 0} findings
                      </span>
                    </div>
                    <div className="rulesmith-test-row">
                      <span className={testResults.safeFindings === 0 ? 'rulesmith-test--pass' : 'rulesmith-test--warn'}>
                        {testResults.safeFindings === 0 ? '✅' : '⚠️'} Safe: {testResults.safeFindings ?? 0} findings
                      </span>
                    </div>
                    <div className="rulesmith-test-row">
                      <span className={testResults.pass ? 'rulesmith-test--pass' : 'rulesmith-test--fail'}>
                        {testResults.pass ? '✅ PASS' : '❌ FAIL'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Calibration results if available */}
                {calResults && (
                  <div className="rulesmith-test-card">
                    <h4>Calibration</h4>
                    <div className="rulesmith-test-row">Total: {calResults.totalFindings} | TP: {calResults.tpCount} | FP: {calResults.fpCount}</div>
                    <div className="rulesmith-test-row">FP Rate: <strong>{calResults.fpRate}</strong></div>
                  </div>
                )}

                {/* Provider info */}
                {displayJob.provider && (
                  <div className="rulesmith-provider">
                    {displayJob.provider} / {displayJob.model}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="rulesmith-actions">
              <button className="vigil-btn-outline rulesmith-btn" onClick={() => setCalJob(displayJob)}>
                ⚗️ Calibrate
              </button>
              <button className="vigil-btn-start rulesmith-btn" onClick={() => handleApprove(displayJob)}
                disabled={displayJob.approved}>
                {displayJob.approved ? '✅ Approved' : '🚀 Approve & Install'}
              </button>
            </div>
          </div>
        )}

        {/* ▸ HISTORY TABLE */}
        <div className="vigil-card rulesmith-history">
          <div className="rulesmith-history__header">
            <h3 className="rulesmith-section-title">📜 Generation History</h3>
            <button className="vigil-btn-outline rulesmith-btn--sm" onClick={loadHistory} disabled={histLoading}>
              {histLoading ? '↻' : '↻ Refresh'}
            </button>
          </div>

          {history.length === 0 && !histLoading && (
            <div className="rulesmith-empty">No rules generated yet. Forge your first rule above!</div>
          )}

          {history.length > 0 && (
            <div className="rulesmith-table-wrap">
              <table className="rulesmith-table">
                <thead>
                  <tr>
                    {[
                      { key: 'description', label: 'Description' },
                      { key: 'ruleId', label: 'Rule ID' },
                      { key: 'languages', label: 'Languages' },
                      { key: 'qualityScore', label: 'Quality' },
                      { key: 'status', label: 'Status' },
                      { key: 'createdAt', label: 'Created' },
                      { key: '_actions', label: '' },
                    ].map(col => (
                      <th key={col.key} className={col.key !== '_actions' ? 'rulesmith-table__sortable' : ''}
                        onClick={() => col.key !== '_actions' && handleSort(col.key)}>
                        {col.label}
                        {sortCol === col.key && <span className="rulesmith-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(job => (
                    <React.Fragment key={job.id}>
                      <tr className={`rulesmith-table__row ${expandedId === job.id ? 'rulesmith-table__row--expanded' : ''}`}
                        onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}>
                        <td className="rulesmith-table__desc">{job.description?.substring(0, 80)}{job.description?.length > 80 ? '…' : ''}</td>
                        <td><code className="rulesmith-rule-id">{job.ruleId || '—'}</code></td>
                        <td>{(job.languages || '').split(',').filter(Boolean).map(l => (
                          <span key={l} className="rulesmith-lang-tag">{l}</span>
                        ))}</td>
                        <td>
                          {job.qualityScore != null
                            ? <span className="rulesmith-score-pill" style={{ color: scoreColor(job.qualityScore) }}>{job.qualityScore}</span>
                            : '—'}
                        </td>
                        <td><span className="rulesmith-status-badge" style={statusBadge(job.status)}>{job.status}</span></td>
                        <td className="rulesmith-table__date">{fmtDate(job.createdAt)}</td>
                        <td className="rulesmith-table__actions" onClick={e => e.stopPropagation()}>
                          <button className="rulesmith-action-btn" title="View details" onClick={() => { setDetailJob(job); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>👁</button>
                          <button className="rulesmith-action-btn" title="Calibrate" onClick={() => setCalJob(job)}>⚗️</button>
                          <button className="rulesmith-action-btn" title="Approve" onClick={() => handleApprove(job)} disabled={job.approved}>🚀</button>
                        </td>
                      </tr>
                      {expandedId === job.id && (
                        <tr className="rulesmith-table__detail-row">
                          <td colSpan={7}>
                            <div className="rulesmith-table__detail">
                              {job.ruleContent && (
                                <div>
                                  <strong>Rule:</strong>
                                  <pre className="rulesmith-code rulesmith-code--sm"><code>{job.ruleContent}</code></pre>
                                </div>
                              )}
                              <div className="rulesmith-table__detail-meta">
                                <span>Validation: {job.validationOk ? '✅' : '❌'} {job.validationMsg || ''}</span>
                                {job.provider && <span>Provider: {job.provider}/{job.model}</span>}
                                {job.error && <span className="rulesmith-error">Error: {job.error}</span>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ▸ CALIBRATION MODAL */}
      {calJob && (
        <CalibrationModal job={calJob} token={token}
          onClose={() => setCalJob(null)}
          onDone={() => { loadHistory(); setCalJob(null); }} />
      )}
    </div>
  );
}
